import { useSearchParams } from 'next/navigation';
import { InfiniteData, useInfiniteQuery, useQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import { useSearchContext } from '@/libraries/contexts/search-context';
import formatUrlSearchParameters from '@/libraries/formatters/format-url-search-parameters';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseQueryHotelsWithOffersData = Offers.Endpoints.HotelsWithOffers;

interface UseQueryHotelsWithOffersOptions {
  initialData?: UseQueryHotelsWithOffersData;
}

interface UseQueryHotelsWithOffersParameters {
  adultsNumber?: string;
  adultsPerRoom?: string;
  batchSize: string;
  bonusCodes?: string;
  brand?: Content.Properties.Brand;
  childrenAges?: string;
  childrenAgesPerRoom?: string;
  departureDate?: string;
  departureIds?: string;
  destinationIds?: string;
  destinationRegionIds?: string;
  durationRange?: string;
  durationType: Content.Components['PeriodType'];
  hotelIds: string;
  skip?: string;
  sortBy?: string;
}

interface UseInfiniteQueryHotelsWithOffersParameters {
  brand?: Content.Properties.Brand;
}

type UseInfiniteQueryHotelsWithOffersData = Offers.Endpoints.HotelsWithOffers;

interface UseInfiniteQueryHotelsWithOffersOptions {
  initialData?: InfiniteData<UseInfiniteQueryHotelsWithOffersData>;
}

export const useQueryHotelsWithOffers = (
  parameters: UseQueryHotelsWithOffersParameters,
  options?: UseQueryHotelsWithOffersOptions,
) => {
  const locale = getLocale();

  const query = useQuery<UseQueryHotelsWithOffersData>(
    [QueryId.SEARCH_SINGLE, parameters, locale],
    async () => {
      const baseUrl = '/api/hotels-with-offers';
      const queryParameters = formatUrlSearchParameters(parameters);

      const fetchResponse = await fetch(
        `${baseUrl}?${queryParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) {
            return response.json();
          }

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQueryHotelsWithOffers', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled:
        !!parameters.departureDate &&
        !!parameters.durationType &&
        !!parameters.hotelIds,
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export const useInfiniteQueryHotelsWithOffers = (
  parameters: UseInfiniteQueryHotelsWithOffersParameters,
  options?: UseInfiniteQueryHotelsWithOffersOptions,
) => {
  const searchParams = useSearchParams();
  const locale = getLocale();
  const { bonusCodes } = useSearchContext();

  const query = useInfiniteQuery<UseInfiniteQueryHotelsWithOffersData>(
    [
      QueryId.HOTELS_WITH_OFFERS,
      parameters,
      locale,
      searchParams!.toString(),
      bonusCodes,
    ],
    async ({ pageParam = 0 }) => {
      const baseUrl = '/api/hotels-with-offers';

      const searchParameters = new URLSearchParams({
        batchSize: String(searchParams!.get('batchSize')),
        departureDate: String(searchParams!.get('departureDate')),
        departureIds: String(searchParams!.get('departureIds')),
        destinationIds: String(searchParams!.get('destinationIds')),
        durationType: String(searchParams!.get('durationType')),
        skip: pageParam.toString(),
        sortBy: String(searchParams!.get('sortBy')),
      });

      if (parameters.brand) {
        searchParameters.append(
          'brand',
          parameters.brand as Content.Properties.Brand,
        );
      }

      if (searchParams!.get('adultsNumber')) {
        searchParameters.append(
          'adultsNumber',
          String(searchParams!.get('adultsNumber')),
        );
      }

      if (searchParams!.get('adultsPerRoom')) {
        searchParameters.append(
          'adultsPerRoom',
          String(searchParams!.get('adultsPerRoom')),
        );
      }

      if (searchParams!.get('childrenAges')) {
        searchParameters.append(
          'childrenAges',
          String(searchParams!.get('childrenAges')),
        );
      }

      if (bonusCodes) {
        searchParameters.append('bonusCodes', String(bonusCodes));
      }

      if (searchParams!.get('childrenAgesPerRoom')) {
        searchParameters.append(
          'childrenAgesPerRoom',
          String(searchParams!.get('childrenAgesPerRoom')),
        );
      }

      if (searchParams!.get('destinationRegionIds')) {
        searchParameters.append(
          'destinationRegionIds',
          String(searchParams!.get('destinationRegionIds')),
        );
      }

      if (searchParams!.get('durationRange')) {
        searchParameters.append(
          'durationRange',
          String(searchParams!.get('durationRange')),
        );
      }

      if (searchParams!.get('filterBoardTypes')) {
        searchParameters.append(
          'filterBoardTypes',
          String(searchParams!.get('filterBoardTypes')),
        );
      }

      if (searchParams!.get('filterCharter')) {
        searchParameters.append(
          'filterCharter',
          String(searchParams!.get('filterCharter')),
        );
      }

      if (searchParams!.get('filterDirect')) {
        searchParameters.append(
          'filterDirect',
          String(searchParams!.get('filterDirect')),
        );
      }

      if (searchParams!.get('filterDiscount')) {
        searchParameters.append(
          'filterDiscount',
          String(searchParams!.get('filterDiscount')),
        );
      }

      if (searchParams!.get('filterFacilityIds')) {
        searchParameters.append(
          'filterFacilityIds',
          String(searchParams!.get('filterFacilityIds')),
        );
      }

      if (searchParams!.get('filterFacilityValues')) {
        searchParameters.append(
          'filterFacilityValues',
          String(searchParams!.get('filterFacilityValues')),
        );
      }

      if (searchParams!.get('filterPricePerPerson')) {
        searchParameters.append(
          'filterPricePerPerson',
          String(searchParams!.get('filterPricePerPerson')),
        );
      }

      if (searchParams!.get('filterStars')) {
        searchParameters.append(
          'filterStars',
          String(searchParams!.get('filterStars')),
        );
      }

      if (searchParams!.get('filterTransportHotel')) {
        searchParameters.append(
          'filterTransportHotel',
          String(searchParams!.get('filterTransportHotel')),
        );
      }

      if (searchParams!.get('hotelIds')) {
        searchParameters.append(
          'hotelIds',
          String(searchParams!.get('hotelIds')),
        );
      }

      const fetchResponse = await fetch(
        `${baseUrl}?${searchParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useInfiniteQueryHotelsWithOffers', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled:
        !!searchParams!.get('departureDate') &&
        !!searchParams!.get('durationType'),
      getNextPageParam: lastPage => {
        const { pagination } = lastPage;
        const totalResults = pagination.count;
        const currentSkip = pagination.skip;
        const currentTake = pagination.take;

        const nextSkip = currentSkip + currentTake;

        if (nextSkip < totalResults) {
          return nextSkip;
        }

        return undefined;
      },
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};
